import * as React from 'react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import type {ReferralFormParam} from 'src/types/referral-external';
import {useHistory} from 'src/rerouter';
import {useDispatch, useSelector} from 'react-redux';
import {
  selectFormData,
  selectBrand,
  selectJobs,
} from 'src/selectors/referral-external';
import * as api from 'src/utils/redux-api-v2';
import {useApi} from 'src/hooks/useApi';
import {AnalyticsService} from 'src/analytics';

import {updatedJobsListing} from 'src/action-creators/referral-external';
import {
  QUESTIONNAIRE_TYPES,
  getReferralUrlSegment,
} from 'src/utils/referral-external';
import {
  prepareReferralApiPayload,
  validate,
  isQuestionVisible,
} from 'src/components/referral-external/candidate-form/form-questions';
import Form from 'src/components/referral-external/candidate-form/form';
import FormModal from 'src/components/referral-external/candidate-form/form-modal';
import RichTextEditor from 'src/components/sense-ai-panel/common/rich-text-editor/rich-text-editor';
import {
  FormInputMedium,
  SubTitleLarge,
  Icon,
} from '@spaced-out/ui-design-system/lib/components/index';
import css from './form.css';


const CandidateFormContainer = ({
  referralLinkId,
  token = '',
  uid,
  utm_source,
  utm_medium,
  isIframe,
}: ReferralFormParam) => {
  const {t, makeKey} = useLabelTranslation();
  const dispatch = useDispatch();
  const router = useHistory();
  const {
    questions,
    must_acknowledge_terms,
    program_name,
    welcome_message,
    is_tracking_enabled,
    track_referral_by_email,
    customer_type,
    is_referrer_portal_enabled,
    job_list_enabled,
  } = useSelector(selectFormData);

  const brand = useSelector(selectBrand);

  const jobs = useSelector(selectJobs);

  const tncContent = useApi(
    `referral/tnc?referral_link_id=${referralLinkId}`,
  ).result;

  const {
    result: userProfileResponse,
    isLoading,
    error,
  } = useApi(
    `referral/referral-form?referral_id=${uid ?? ''}&token=${token ?? ''}`,
    {
      shouldFetch: !!uid && !!token,
    },
  );

  const referrerName = decodeURIComponent(
    new URLSearchParams(window.location.search).get('referrerName') || '',
  );
  const referrerEmail = decodeURIComponent(
    new URLSearchParams(window.location.search).get('referrerEmail') || '',
  );

  const referrerQuestions = questions.filter(
    (question) =>
      question.is_question_set &&
      question.display_section === 'referrer' &&
      isQuestionVisible(token || '', question),
  );

  const initialAnswers = isIframe
    ? new Map([
        // referrer_name
        [referrerQuestions[0].id, referrerName ?? referrerEmail],
        // referred_by
        [referrerQuestions[1].id, referrerEmail],
      ])
    : new Map();

  const initialTncAccepted = isIframe ? true : false;

  const [answers, setAnswers] = React.useState(initialAnswers);
  const [touched, setTouched] = React.useState(new Map());
  const [submitting, setSubmitting] = React.useState(false);

  const [showTnc, setShowTnc] = React.useState(false);
  const [errors, setErrors] = React.useState(new Map());
  const [apiError, setApiError] = React.useState<any>(null);
  const [tncError, setTncError] = React.useState<any>(null);
  const [tncAccepted, setTncAccepted] = React.useState(initialTncAccepted);
  const [showWithdrawConfirmation, setShowWithdrawConfirmation] =
    React.useState(false);

  const handleQuestionResponseChange = (
    questionId: number,
    fieldType: string,
    value: string | null | undefined | Array<string>,
  ) => {
    if (fieldType === QUESTIONNAIRE_TYPES.job && value) {
      // @ts-expect-error - TS2554 - Expected 2 arguments, but got 1.
      AnalyticsService.track('Job Assigned');
    }

    const newAnswers = new Map(answers);
    newAnswers.set(questionId, value);
    setAnswers(newAnswers);
    const newTouched = new Map(touched);
    newTouched.set(questionId, true);
    setTouched(newTouched);
    const errors = validate(questions, newAnswers, newTouched, token ?? '', t);
    setErrors(errors);
  };

  React.useEffect(() => {
    if (error) {
      router.push({
        pathname: getReferralUrlSegment(referralLinkId),
        query: {action: 'token-invalid'},
      });
    }
    if (!isLoading && !userProfileResponse) {
      return;
    }

    const newAnswers = new Map(answers);
    questions.forEach((item) => {
      if (item.response_field === 'communication_mode') {
        newAnswers.set(item.id, 'Email');
      }
      // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'ResponseFieldName' can't be used to index type '{}'.
      if (userProfileResponse && userProfileResponse[item.response_field]) {
        // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'ResponseFieldName' can't be used to index type '{}'.
        newAnswers.set(item.id, userProfileResponse[item.response_field]);
      }
    });
    setAnswers(newAnswers);
  }, [userProfileResponse, questions, error]);

  React.useEffect(() => {
    const agentId = '';
    if (token) {
      dispatch(
        updatedJobsListing(
          referralLinkId,
          agentId,
          {
            search_str: '',
            location: [],
          },
          job_list_enabled,
        ),
      );
    }
  }, []);

  const handleCandidateFormSubmit = async () => {
    if (submitting) {
      return;
    }
    // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
    const reachOutToReferralId = questions.find(
      (question) =>
        question.response_field === QUESTIONNAIRE_TYPES.reachOutToReferral,
    ).id;

    // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
    const referrerEmailQuestionId = questions.find(
      (question) =>
        question.response_field === QUESTIONNAIRE_TYPES.referrerEmail,
    ).id;

    const sourceEmail = answers.get(referrerEmailQuestionId);

    if (!answers.has(reachOutToReferralId)) {
      answers.set(reachOutToReferralId, 'Email');
    }
    const touchedField = new Map(touched);
    questions.forEach((question) => touchedField.set(question.id, true));
    setTouched(touchedField);
    const errors = validate(questions, answers, touchedField, token ?? '', t);
    if (errors.size > 0) {
      setErrors(errors);
      return;
    }
    if (!token && must_acknowledge_terms && !tncAccepted) {
      //how to show must accept terms error?
      setTncError('You must accept Terms and Conditions before submitting.');
      return;
    }

    const updatedQuery = {
      action: 'success',
      token: token ?? '',
      utm_medium: utm_medium || '',
      utm_source: utm_source || '',
    } as const;

    if (is_referrer_portal_enabled) {
      // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type '"source_email"' can't be used to index type '{ readonly action: "success"; readonly token: string; readonly utm_medium: string; readonly utm_source: string; }'.
      updatedQuery['source_email'] =
        typeof sourceEmail === 'string' ? sourceEmail : '';
    }

    const payload = prepareReferralApiPayload(
      questions,
      answers,
      referralLinkId,
      // @ts-expect-error - TS2345 - Argument of type 'JobResponse[]' is not assignable to parameter of type 'Job[]'.
      jobs,
      utm_source,
      utm_medium,
    );

    try {
      setSubmitting(true);
      token
        ? await dispatch(
            api.put(
              `referral/referral-form?referral_id=${uid ?? ''}&token=${
                token || ''
              }`,
              {
                data: {
                  ...payload,
                },
              },
            ),
          )
        : await dispatch(
            api.post(`referral/submit`, {
              ...payload,
            }),
          );
      setAnswers(new Map());
      setTouched(new Map());
      setErrors(new Map());
      router.push({
        pathname: getReferralUrlSegment(referralLinkId),
        query: updatedQuery,
      });
    } catch (ex: any) {
      //show error message in popup
      setApiError(ex.responseBody.message);
      if (ex.responseBody?.error_code === 'DUPLICATE_REFERRAL') {
        setErrors(new Map());
      }
    } finally {
      setSubmitting(false);
    }
  };
  const handleCandidateFormCancel = () => {
    setAnswers(new Map());
    setTouched(new Map());
  };
  const handleTncDialogClose = () => {
    setShowTnc(false);
  };
  const handleCloseErrorMessage = () => {
    setApiError(null);
  };
  const handleWitdhdraw = () => {
    setShowWithdrawConfirmation(true);
  };
  const handleWitdhdrawSave = async () => {
    try {
      setShowWithdrawConfirmation(false);
      setSubmitting(true);
      await dispatch(
        api.put(
          `referral/referral-form?referral_id=${uid ?? ''}&token=${
            token || ''
          }`,
          {
            data: {
              is_withdrawn: true,
            },
          },
        ),
      );
      setAnswers(new Map());
      router.push({
        pathname: getReferralUrlSegment(referralLinkId),
        query: {action: 'withdraw-success'},
      });
    } catch (ex: any) {
      //show error message in popup
      setApiError(ex.responseBody.message);
      if (ex.responseBody?.error_code === 'DUPLICATE_REFERRAL') {
        //clear the form
        setAnswers(new Map());
      }
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <>
      <Form
        questions={questions}
        answers={answers}
        brandName={brand.display_name}
        brandButtonColor={brand.button_color}
        program_name={program_name}
        welcome_message={welcome_message}
        is_tracking_enabled={is_tracking_enabled}
        track_referral_by_email={track_referral_by_email}
        customer_type={customer_type}
        onQuestionResponseChange={handleQuestionResponseChange}
        onSubmit={handleCandidateFormSubmit}
        onCancel={handleCandidateFormCancel}
        openTnc={() => setShowTnc(true)}
        handleWitdhdraw={handleWitdhdraw}
        referralLinkId={referralLinkId}
        errors={errors}
        tncAccepted={tncAccepted}
        tncError={tncError}
        setTncError={setTncError}
        must_acknowledge_terms={must_acknowledge_terms}
        setTncAccepted={setTncAccepted}
        submitting={submitting}
        token={token}
        // @ts-expect-error - TS2322 - Type 'unknown' is not assignable to type 'CandidateProfile | null | undefined'.
        userProfileResponse={userProfileResponse}
        demoForm={false}
        isIframe={isIframe}
      />
      <FormModal
        isOpen={showTnc && !token}
        header={t('Terms_and_Conditions', 'Terms and Conditions')}
        onClose={handleTncDialogClose}
        size="large"
      >
        <RichTextEditor
          // @ts-expect-error - TS2339 - Property 'terms_and_conditions' does not exist on type '{}'.
          value={tncContent?.terms_and_conditions ?? ''}
          readOnly={true}
          height="100%"
          borderRadius="0px"
        />
      </FormModal>
      <FormModal
        isOpen={!!apiError}
        header="Error"
        onClose={handleCloseErrorMessage}
      >
        <FormInputMedium>{t(makeKey(apiError), apiError)}</FormInputMedium>
      </FormModal>
      <FormModal
        header={
          <SubTitleLarge className={css.dialogHeaderContent}>
            <Icon name="info-circle" />
            {` ${t('Withdraw_Candidature', 'Withdraw Candidature')}`}
          </SubTitleLarge>
        }
        onClose={() => setShowWithdrawConfirmation(false)}
        onSubmit={handleWitdhdrawSave}
        showFooter={true}
        isOpen={showWithdrawConfirmation}
      >
        <FormInputMedium>{`${t(
          'Are_you_sure_you_want_to_withdraw_your_candidature_from_this_referral_submission',
          'Are you sure you want to withdraw your candidature from this referral submission',
        )}?`}</FormInputMedium>
      </FormModal>
    </>
  );
};

export default CandidateFormContainer;
